import {
  CheckCircle,
  Clock,
  Info,
  MessageSquare,
  Sparkles,
  TrendingUp,
  XCircle,
} from "lucide-react";
import React from "react";
import { cn } from "../../utils/cn";
import { ResponsiveGrid, Tooltip } from "../index";

interface Stats {
  total_posts: number;
  pending_approval: number;
  approved: number;
  rejected: number;
  published: number;
  approval_rate: number;
}

interface StatsBarProps {
  stats: Stats;
}

const StatsBar: React.FC<StatsBarProps> = ({ stats }) => {
  const statsItems: Array<{
    title: string;
    value: string | number | React.ReactNode;
    icon: React.ReactNode;
    color: "blue" | "green" | "red" | "orange" | "purple" | "indigo" | "yellow";
    tooltip: string;
  }> = [
    {
      title: "Total",
      value: stats.total_posts,
      icon: <MessageSquare className="h-5 w-5" />,
      color: "blue",
      tooltip:
        "Total de posts em todos os status (pendentes, aprovados, publicados, rejeitados e agendados)",
    },
    {
      title: "Pendentes",
      value: stats.pending_approval,
      icon: <Clock className="h-5 w-5" />,
      color: "orange",
      tooltip: "Posts aguardando aprovação para publicação",
    },
    {
      title: "Aprovados",
      value: stats.approved,
      icon: <CheckCircle className="h-5 w-5" />,
      color: "green",
      tooltip: "Posts aprovados e prontos para publicação",
    },
    {
      title: "Rejeitados",
      value: stats.rejected,
      icon: <XCircle className="h-5 w-5" />,
      color: "red",
      tooltip: "Posts rejeitados durante o processo de aprovação",
    },
    {
      title: "Publicados",
      value: stats.published,
      icon: <TrendingUp className="h-5 w-5" />,
      color: "purple",
      tooltip: "Posts já publicados nas redes sociais",
    },
    {
      title: "Taxa Aprovação",
      value: `${Math.round(stats.approval_rate)}%`,
      icon: <Sparkles className="h-5 w-5" />,
      color: "purple",
      tooltip:
        "Percentual de posts aprovados em relação aos avaliados (aprovados + rejeitados)",
    },
  ];

  const CARD_COLORS = {
    blue: {
      border: "border-blue-200",
      text: "text-blue-700",
      iconBg: "bg-blue-50/50",
      iconText: "text-blue-600",
      topBar: "bg-blue-400",
    },
    orange: {
      border: "border-orange-200",
      text: "text-orange-700",
      iconBg: "bg-orange-50/50",
      iconText: "text-orange-600",
      topBar: "bg-orange-400",
    },
    green: {
      border: "border-green-200",
      text: "text-green-700",
      iconBg: "bg-green-50/50",
      iconText: "text-green-600",
      topBar: "bg-green-400",
    },
    red: {
      border: "border-red-200",
      text: "text-red-700",
      iconBg: "bg-red-50/50",
      iconText: "text-red-600",
      topBar: "bg-red-400",
    },
    purple: {
      border: "border-purple-200",
      text: "text-purple-700",
      iconBg: "bg-purple-50/50",
      iconText: "text-purple-600",
      topBar: "bg-purple-400",
    },
  };

  return (
    <ResponsiveGrid
      cols={{ default: 2, sm: 3, md: 6, lg: 6, xl: 6 }}
      gap="sm"
      className="mb-1 w-full"
    >
      {statsItems.map((item, index) => {
        const c =
          CARD_COLORS[item.color as keyof typeof CARD_COLORS] ||
          CARD_COLORS.purple;
        return (
          <div key={index} className="w-full">
            <div
              className={cn(
                "relative bg-white rounded-xl border p-2 shadow-xs transition-all duration-200 flex flex-col justify-between overflow-hidden group hover:scale-[1.01] hover:shadow-md min-h-[60px]",
                c.border,
              )}
            >
              {/* Colored top accent bar */}
              <div
                className={cn(
                  "absolute top-0 left-0 right-0 h-[2.5px]",
                  c.topBar,
                )}
              />

              <div className="flex items-center justify-between gap-1 w-full mt-0.5">
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-0.5 truncate flex items-center gap-1">
                    {item.title}
                    <Tooltip
                      content={item.tooltip}
                      position="top"
                      wrap
                      usePortal
                      zIndex={10050}
                    >
                      <Info className="h-2.5 w-2.5 text-gray-400 hover:text-purple-600 transition-colors cursor-help shrink-0" />
                    </Tooltip>
                  </span>
                  <span
                    className={cn(
                      "text-base font-black tracking-tight leading-none",
                      c.text,
                    )}
                  >
                    {item.value}
                  </span>
                </div>
                <div
                  className={cn(
                    "w-7 h-7 rounded-lg flex items-center justify-center shrink-0 border border-purple-100/50",
                    c.iconBg,
                    c.iconText,
                  )}
                >
                  {React.cloneElement(
                    item.icon as React.ReactElement<{ className?: string }>,
                    {
                      className: "h-3.5 w-3.5",
                    },
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </ResponsiveGrid>
  );
};

export default StatsBar;
